import React from 'react';
import { ArrowRight, Star, Zap, Crown } from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAuth } from '../contexts/AuthContext';

interface UpgradePromptCardProps {
  onUpgrade: (plan?: string) => void;
  isLimitReached?: boolean;
}

const UpgradePromptCard: React.FC<UpgradePromptCardProps> = ({ onUpgrade, isLimitReached = false }) => {
  const { trackUpgradePromptShown, trackUpgradePromptClicked } = useAnalytics();
  const { userProfile } = useAuth();
  
  // Track when upgrade prompt is shown - only if user has actually used the product (processed at least 1 video)
  React.useEffect(() => {
    // Only track if user has processed at least 1 video (they've actually used the product)
    if (userProfile && userProfile.videosProcessed > 0) {
      const context = isLimitReached ? 'limit_reached' : 'upgrade_card';
      trackUpgradePromptShown(context, userProfile?.plan || 'free');
    }
  }, [isLimitReached, trackUpgradePromptShown, userProfile]);
  
  const handleUpgradeClick = (plan?: string) => {
    const context = isLimitReached ? 'limit_reached' : 'upgrade_card';
    trackUpgradePromptClicked(context, plan || 'creator');
    onUpgrade(plan);
  };
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center mr-4">
            {isLimitReached ? <Zap className="w-5 h-5 text-white" /> : <Crown className="w-5 h-5 text-white" />}
          </div>
          <div>
            <h3 className="text-xl font-bold text-black">
              {isLimitReached ? 'Limit Reached!' : 'Unlock Your Potential'}
            </h3>
            <p className="text-gray-600 text-sm">
              {isLimitReached 
                ? 'Upgrade to continue creating clips'
                : 'Professional tools for content creators'
              }
            </p>
          </div>
        </div>
        
        {/* Key Benefits */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="flex items-center">
            <Star className="w-4 h-4 mr-2 text-red-500" />
            <span className="text-sm text-gray-700">No watermarks</span>
          </div>
          <div className="flex items-center">
            <Star className="w-4 h-4 mr-2 text-red-500" />
            <span className="text-sm text-gray-700">Priority processing</span>
          </div>
          <div className="flex items-center">
            <Star className="w-4 h-4 mr-2 text-red-500" />
            <span className="text-sm text-gray-700">More videos</span>
          </div>
          <div className="flex items-center">
            <Star className="w-4 h-4 mr-2 text-red-500" />
            <span className="text-sm text-gray-700">Advanced AI</span>
          </div>
        </div>

        {/* Pricing teaser */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
          <div className="text-center">
            <div className="text-sm text-gray-600">Plans starting at</div>
            <div className="text-2xl font-bold text-black">$9.99<span className="text-sm text-gray-600">/month</span></div>
          </div>
        </div>
        
         {/* CTA Button */}
         <button 
           onClick={() => handleUpgradeClick()}
           className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center group"
         >
          <span>Choose Your Plan</span>
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
        </button>
        
        <div className="flex items-center justify-center mt-3 text-xs text-gray-500">
          <span>Cancel anytime • 30-day money back guarantee</span>
        </div>
    </div>
  );
};

export default UpgradePromptCard; 